import React, { useEffect, useState } from 'react';
import styles from './styles.module.scss';
import Flex from 'src/components/Flex';
import Card from 'src/components/Card';
import Button from 'src/components/Button';
import Input from 'src/components/Input';
import Icon from 'src/components/Icon';
import { SurveyMigrationConfig } from 'src/api/surveyMigration';
import {
    useSaveSurveyMigrationConfig,
    useValidateSurveyMigrationConnection,
} from 'src/queries/useSurveyMigration';

interface ConfigurationSectionProps {
    config: SurveyMigrationConfig | null;
}

function maskApiKey(apiKey: string): string {
    const last4 = apiKey.slice(-4);
    return `••••••••${last4}`;
}

function errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.';
}

function ConfigurationSection({ config }: ConfigurationSectionProps) {
    const isConfigured = !!config?.workspaceId;
    const [isEditing, setIsEditing] = useState(!isConfigured);
    const [workspaceId, setWorkspaceId] = useState(config?.workspaceId ?? '');
    const [apiKey, setApiKey] = useState('');
    const [surveyTemplateVersionId, setSurveyTemplateVersionId] = useState(config?.surveyTemplateVersionId ?? '');

    useEffect(() => {
        if (config?.workspaceId) {
            setWorkspaceId(config.workspaceId);
            setSurveyTemplateVersionId(config.surveyTemplateVersionId ?? '');
            setIsEditing(false);
        }
    }, [config?.workspaceId, config?.surveyTemplateVersionId]);

    const saveConfig = useSaveSurveyMigrationConfig();
    const validateConnection = useValidateSurveyMigrationConnection();

    const handleSave = async () => {
        await saveConfig.mutateAsync({ workspaceId, apiKey, surveyTemplateVersionId, isValidated: false });
        setApiKey('');
        setIsEditing(false);
    };

    const handleValidate = () => {
        if (!config?.workspaceId) return;
        validateConnection.mutate({
            workspaceId: config.workspaceId,
            apiKey: config.apiKey ?? '',
            isValidated: false,
        });
    };

    const handleEdit = () => {
        setIsEditing(true);
        setWorkspaceId(config?.workspaceId ?? '');
        setApiKey('');
        setSurveyTemplateVersionId(config?.surveyTemplateVersionId ?? '');
        validateConnection.reset();
    };

    const renderConnectionStatus = () => {
        if (validateConnection.isLoading) {
            return (
                <Flex direction="row" align="center" className={styles.statusRow}>
                    <Icon type="fa" name="spinner" spin className={styles.statusSpinner} />
                    <span>Validating connection…</span>
                </Flex>
            );
        }
        if (validateConnection.isError) {
            return (
                <Flex direction="row" align="center" className={styles.statusRow}>
                    <Icon type="fa" name="times-circle" className={styles.statusFailed} />
                    <span className={styles.statusFailed}>{errorMessage(validateConnection.error)}</span>
                </Flex>
            );
        }
        if (validateConnection.isSuccess) {
            if (validateConnection.data) {
                return (
                    <Flex direction="row" align="center" className={styles.statusRow}>
                        <Icon type="fa" name="check-circle" className={styles.statusVerified} />
                        <span className={styles.statusVerified}>Connection verified</span>
                    </Flex>
                );
            }
            return (
                <Flex direction="row" align="center" className={styles.statusRow}>
                    <Icon type="fa" name="times-circle" className={styles.statusFailed} />
                    <span className={styles.statusFailed}>
                        Connection failed. Check the Workspace ID and API Key, then try again.
                    </span>
                </Flex>
            );
        }
        if (config?.isValidated) {
            return (
                <Flex direction="row" align="center" className={styles.statusRow}>
                    <Icon type="fa" name="check-circle" className={styles.statusVerified} />
                    <span className={styles.statusVerified}>Connection verified</span>
                </Flex>
            );
        }
        return null;
    };

    return (
        <Card className={styles.cardWrap} label="Configuration">
            <Flex direction="column" className={styles.sectionBody}>
                <Input
                    label="Workspace ID"
                    value={workspaceId}
                    disabled={!isEditing || saveConfig.isLoading}
                    placeholder="Enter your Graphium Surveys workspace ID"
                    onChangeText={(value: string) => setWorkspaceId(value)}
                />
                {isEditing ? (
                    <Input
                        label="API Key"
                        type="password"
                        value={apiKey}
                        disabled={saveConfig.isLoading}
                        placeholder="Enter the API key"
                        onChangeText={(value: string) => setApiKey(value)}
                    />
                ) : (
                    <Input label="API Key" value={config?.apiKey ? maskApiKey(config.apiKey) : ''} disabled />
                )}
                <Input
                    label="Survey Template Version ID"
                    value={surveyTemplateVersionId}
                    disabled={!isEditing || saveConfig.isLoading}
                    placeholder="Enter the survey template version ID"
                    onChangeText={(value: string) => setSurveyTemplateVersionId(value)}
                />

                {saveConfig.isError && <div className={styles.error}>{errorMessage(saveConfig.error)}</div>}

                <Flex direction="row" align="center" className={styles.buttonRow}>
                    {isEditing ? (
                        <Button
                            type="primary"
                            text="Save Configuration"
                            isLoading={saveConfig.isLoading}
                            disabled={!workspaceId || !apiKey || !surveyTemplateVersionId}
                            onClick={handleSave}
                        />
                    ) : (
                        <Button type="outline" text="Edit" onClick={handleEdit} />
                    )}
                    {isConfigured && !isEditing && (
                        <Button
                            type="secondary"
                            text="Validate Connection"
                            isLoading={validateConnection.isLoading}
                            onClick={handleValidate}
                        />
                    )}
                </Flex>

                {!isEditing && renderConnectionStatus()}
            </Flex>
        </Card>
    );
}

export default ConfigurationSection;
